/**
 * Arrival-readiness backend error codes.
 *
 * Must match `ErrorCodes` in be/utils/errors.js (Arrival Readiness group).
 *
 * Codes are the stable API contract; the backend's `error` string is Arabic and
 * exists for legacy clients and logs. Translation lives in the locale files
 * (`arrivalReadiness.*` keys) — these are just the identifiers.
 *
 * Client rule: if a response carries `code`, translate it; otherwise fall back
 * to displaying the server's message. Never infer meaning from message text.
 *
 * Pattern reference: web/src/utils/taskErrorCodes.js
 */
export const ARRIVAL_ERROR_CODES = {
  BOOKING_NOT_FOUND: 'ARRIVAL_001',
  // The booking's message IS automated (it has an OTA thread), so a manual-send
  // assertion is refused — it would hide a real automation failure. Seeing this
  // means the row offering the tick was stale.
  MANUAL_SEND_NOT_APPLICABLE: 'ARRIVAL_002',
  // Ops check-in confirmation (tasks/manual-guest-arrival-confirm.md).
  // Cancelled booking — nobody stayed, so nobody arrived.
  CHECKIN_NOT_APPLICABLE: 'ARRIVAL_003',
  // Missing, future, or outside the stay — rejected, never clamped.
  CHECKIN_TIME_INVALID: 'ARRIVAL_004'
};

/**
 * Message-column status markers (be/services/checkinMessage/readiness.js).
 * Not error codes, but the same contract rule: the card branches on THESE, never
 * on the Arabic copy beside them.
 */
export const ARRIVAL_MESSAGE_CODES = {
  MANUAL_SEND_REQUIRED: 'manual_send_required',
  MANUAL_SEND_DONE: 'manual_send_done'
};

/**
 * Guest-entry evidence states, mirroring `STATUS` in
 * be/services/guestArrival/resolver.js. Same contract rule as above.
 *
 * Only ARRIVED is ever rendered. NO_EVIDENCE and NOT_APPLICABLE are listed so a
 * reader can see they EXIST and are deliberately silent: silence from the lock is
 * equally "he hasn't come yet" and "he is at the door on a code that failed", so
 * neither may be shown as reassurance.
 */
export const GUEST_ENTRY_STATUS = {
  ARRIVED: 'ARRIVED',
  NO_EVIDENCE: 'NO_EVIDENCE',
  NOT_APPLICABLE: 'NOT_APPLICABLE'
};

export default ARRIVAL_ERROR_CODES;
